import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import type { FiscalDeviceDto, FiscalDeviceIdentityDto } from "@finora/types";

/**
 * Puna implementacija po lokaciji (25.09.2026) - svaka kasa/lokacija ima
 * SOPSTVENI PFX sertifikat, PAK i lozinku (razlikuje se od sertifikata na
 * nivou firme iznad na ovoj strani, koji ostaje za firme sa JEDNOM
 * lokacijom - vidi napomenu u pfr-fiscalization.provider.ts::resolveFiscalCredentials).
 *
 * businessLocationId se NIKAD ne pogađa automatski - korisnik ga ovde ručno
 * potvrđuje za svaki uređaj (status PENDING_LOCATION -> ACTIVE).
 */
export function FiscalDevicesSection() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [name, setName] = useState("");
  const [businessLocationId, setBusinessLocationId] = useState("");
  const [preview, setPreview] = useState<FiscalDeviceIdentityDto | null>(null);

  const { data: devices } = useQuery({
    queryKey: ["fiscal-devices"],
    queryFn: () => api.listFiscalDevices(),
  });
  const { data: locations } = useQuery({
    queryKey: ["business-location-options"],
    queryFn: () => api.listBusinessLocationOptions(),
  });

  const previewMutation = useMutation({
    mutationFn: (file: File) => api.previewFiscalDeviceIdentity(file, password),
    onSuccess: (result) => setPreview(result),
  });

  const registerMutation = useMutation({
    mutationFn: () => {
      if (!pendingFile) throw new Error("Nije izabran sertifikat.");
      return api.registerFiscalDevice(pendingFile, {
        password,
        apiKey,
        businessLocationId: businessLocationId || null,
        name: name || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-devices"] });
      setPendingFile(null);
      setPreview(null);
      setPassword("");
      setApiKey("");
      setName("");
      setBusinessLocationId("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
  });

  const assignLocationMutation = useMutation({
    mutationFn: ({ id, businessLocationId }: { id: string; businessLocationId: string }) =>
      api.assignFiscalDeviceLocation(id, businessLocationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fiscal-devices"] }),
  });

  return (
    <div className="mt-5 border-t border-border pt-4">
      <h2 className="mb-1 text-sm font-semibold text-slate-800">Fiskalni uređaji (kase po lokaciji)</h2>
      <p className="mb-3 text-xs text-slate-500">
        Ako firma ima više poslovnih prostora, svaki poseduje SOPSTVENI PFX sertifikat i PAK - registruj
        ovde svaku kasu posebno. Za firmu sa jednom lokacijom nije potrebno ništa dodavati ovde - koristi
        se sertifikat sa vrha ove strane.
      </p>

      <div className="rounded-lg border border-dashed border-border p-4">
        <label className="mb-1 block text-sm font-medium text-slate-700">Sertifikat kase (.pfx/.p12)</label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pfx,.p12"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            setPendingFile(file);
            setPreview(null);
          }}
        />
        <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
          {pendingFile ? pendingFile.name : "Izaberi sertifikat"}
        </Button>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Lozinka sertifikata</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">PAK/PAC kod</label>
            <input
              type="text"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-3">
          <Button
            variant="secondary"
            disabled={!pendingFile || previewMutation.isPending}
            onClick={() => pendingFile && previewMutation.mutate(pendingFile)}
          >
            {previewMutation.isPending ? "Očitavam..." : "Prepoznaj kasu iz sertifikata"}
          </Button>
        </div>

        {previewMutation.isError && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {previewMutation.error instanceof Error ? previewMutation.error.message : "Očitavanje nije uspelo."}
          </p>
        )}

        {preview && (
          <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <p className="font-medium">Fiskalni sertifikat</p>
            <p className="text-xs">ЈИД: {preview.jid ?? "-"}</p>
            <p className="text-xs">Obveznik: {preview.companyName ?? "-"}</p>
            <p className="text-xs">
              Poslovni prostor: {preview.businessLocationCode ?? "-"} - {preview.businessLocationName ?? "-"}
            </p>
            <p className="text-xs">Adresa: {preview.address ?? "-"}</p>
            <p className="text-xs">Mesto: {preview.city ?? "-"}</p>
            <p className="text-xs">PIB: {preview.pib ?? "-"}</p>
            {preview.validTo && (
              <p className="text-xs">Važi do: {new Date(preview.validTo).toLocaleDateString("sr-RS")}</p>
            )}
          </div>
        )}

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Naziv kase (opciono)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="npr. Kasa 1"
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Poslovna lokacija</label>
            <select
              value={businessLocationId}
              onChange={(e) => setBusinessLocationId(e.target.value)}
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            >
              <option value="">Potvrdi kasnije (ostaje na čekanju)</option>
              {locations?.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            disabled={!pendingFile || !apiKey || registerMutation.isPending}
            onClick={() => registerMutation.mutate()}
          >
            {registerMutation.isPending ? "Registrujem..." : "Registruj kasu"}
          </Button>
        </div>

        {registerMutation.isError && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {registerMutation.error instanceof Error ? registerMutation.error.message : "Registracija nije uspela."}
          </p>
        )}
        {registerMutation.isSuccess && (
          <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Kasa je registrovana.
          </p>
        )}
      </div>

      {devices && devices.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-slate-400">
                <th className="py-2 pr-3">Kasa</th>
                <th className="py-2 pr-3">ЈИД</th>
                <th className="py-2 pr-3">Poslovni prostor (sertifikat)</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Lokacija</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device: FiscalDeviceDto) => (
                <tr key={device.id} className="border-b border-border/60">
                  <td className="py-2 pr-3">{device.name ?? "-"}</td>
                  <td className="py-2 pr-3">{device.jid ?? "-"}</td>
                  <td className="py-2 pr-3">
                    {device.businessLocationCodeFromCertificate ?? "-"} -{" "}
                    {device.businessLocationNameFromCertificate ?? "-"}
                  </td>
                  <td className="py-2 pr-3">
                    {device.status === "ACTIVE" && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        Aktivna
                      </span>
                    )}
                    {device.status === "PENDING_LOCATION" && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                        Čeka potvrdu lokacije
                      </span>
                    )}
                    {device.status === "INACTIVE" && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                        Neaktivna
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      value={device.businessLocationId ?? ""}
                      onChange={(e) => {
                        if (e.target.value) {
                          assignLocationMutation.mutate({ id: device.id, businessLocationId: e.target.value });
                        }
                      }}
                      className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                    >
                      <option value="">- izaberi -</option>
                      {locations?.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
